import { useEffect, useRef, useState } from 'react'
import type { CommonCode, Region } from '@/shared/lib/apiTypes'
import { EMPTY_FILTER as EMPTY, type FilterValue as FV } from '@/shared/lib/filterValue'
import {
  DISMISS_DISTANCE_RATIO,
  DISMISS_VELOCITY_PX_PER_S,
  exitDelayMs,
  prefersReducedMotion,
} from '@/shared/lib/animation'
import { figmaLabel, isHalfWidthCategory, sortByFigma } from '@/shared/lib/figmaLabel'
import { Selectbutton } from '@/shared/ui/Selectbutton'
import { useDismissable } from '@/shared/composables/useDismissable'

export { EMPTY_FILTER, filterCount, type FilterValue } from '@/shared/lib/filterValue'
import { TABS, type Tab } from '@/public/features/problem/constants/filterTabs'

export function ProblemFilterSheet({
  value,
  categories,
  lifecycle,
  situation,
  dongs,
  onApply,
  onClose,
}: {
  value: FV
  categories: CommonCode[]
  lifecycle: CommonCode[]
  situation: CommonCode[]
  dongs: Region[]
  onApply: (next: FV) => void
  onClose: () => void
}) {
  const [tab, setTab] = useState<Tab>('category')
  const [closing, setClosing] = useState(false)
  const [draft, setDraft] = useState<FV>(value)
  const sheetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    sheetRef.current?.focus()
  }, [])

  const requestClose = (after?: () => void) => {
    setClosing(true)
    window.setTimeout(() => {
      after?.()
      onClose()
    }, exitDelayMs())
  }

  const drag = useRef<{ y: number; t: number; last: number; lastT: number } | null>(null)

  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId)
    const now = performance.now()
    drag.current = { y: e.clientY, t: now, last: e.clientY, lastT: now }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current
    const sheet = sheetRef.current
    if (!d || !sheet) return
    const dy = Math.max(0, e.clientY - d.y)
    d.last = e.clientY
    d.lastT = performance.now()
    if (prefersReducedMotion()) return
    sheet.style.transform = `translateY(${dy}px)`
  }

  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current
    const sheet = sheetRef.current
    drag.current = null
    if (!d || !sheet) return

    const dy = Math.max(0, e.clientY - d.y)
    const elapsed = Math.max(1, performance.now() - d.lastT)
    const velocity = ((e.clientY - d.last) / elapsed) * 1000
    const far = dy > sheet.offsetHeight * DISMISS_DISTANCE_RATIO
    const fast = velocity > DISMISS_VELOCITY_PX_PER_S

    if (far || fast) {
      requestClose()
      return
    }
    // 제자리로 — 되돌아가는 것은 «들어오는» 움직임이라 진입 값을 쓴다.
    sheet.style.transition = 'transform var(--enter-ms) var(--enter-easing)'
    sheet.style.transform = ''
    window.setTimeout(() => {
      sheet.style.transition = ''
    }, exitDelayMs() + 1)
  }

  useDismissable(() => requestClose())

  const toggle = (axis: keyof FV, code: string) => {
    setDraft((d) => ({
      ...d,
      [axis]: d[axis].includes(code) ? d[axis].filter((c) => c !== code) : [...d[axis], code],
    }))
  }

  return (
    <div className={closing ? 'ui-overlay group is-closing' : 'ui-overlay group'}>
      <div className="ui-dimmed ui-overlay__dim animate-dim-in group-[.is-closing]:animate-dim-out" onClick={() => requestClose()} aria-hidden="true" />

      <div
        ref={sheetRef}
        tabIndex={-1}
        className="absolute inset-x-0 bottom-0 flex h-[550px] animate-sheet-in flex-col gap-2 overflow-hidden rounded-t-[20px] bg-white-50 pb-3 group-[.is-closing]:animate-sheet-out"
        role="dialog"
        aria-modal="true"
        aria-label="시민문제 필터"
      >
        <button
          type="button"
          className="ui-pressable flex min-h-12 cursor-grab items-center justify-center border-0 bg-none py-3 touch-none"
          aria-label="필터 닫기"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onClick={() => requestClose()}
        >
          <span className="h-1.5 w-[90px] rounded-[20px] bg-white-300" />
        </button>

        <div className="flex shrink-0 items-end gap-2 px-4" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={
                tab === t.key
                  ? 'ui-pressable min-w-0 flex-1 shrink-0 grow basis-0 cursor-pointer border-0 border-b-2 bg-none px-4 py-[9px] text-body-md border-b-violet-500 font-semibold text-[#2a303c]'
                  : 'ui-pressable min-w-0 flex-1 shrink-0 grow basis-0 cursor-pointer border-0 border-b-2 bg-none px-4 py-[9px] text-body-md border-b-transparent text-black-100'
              }
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex min-h-0 flex-1 shrink-0 grow basis-0 flex-col gap-3.5 overflow-y-auto px-4 py-1 [&>*]:animate-fade-in">
          {tab === 'category' && (
            <ChipGroup
              items={sortByFigma(categories).map((c) => ({
                code: c.code,
                label: figmaLabel(c.code, c.label),
                half: isHalfWidthCategory(c.code),
              }))}
              selected={draft.categories}
              onToggle={(code) => toggle('categories', code)}
            />
          )}

          {tab === 'target' && (
            <>
              <Labelled label="연령대">
                <ChipGroup
                  items={lifecycle.map((c) => ({ code: c.code, label: figmaLabel(c.code, c.label) }))}
                  selected={draft.targets}
                  onToggle={(code) => toggle('targets', code)}
                />
              </Labelled>
              <Labelled label="상황 및 특성">
                <ChipGroup
                  items={situation.map((c) => ({ code: c.code, label: figmaLabel(c.code, c.label) }))}
                  selected={draft.targets}
                  onToggle={(code) => toggle('targets', code)}
                />
              </Labelled>
            </>
          )}

          {tab === 'region' && (
            <ChipGroup
              items={dongs.map((r) => ({ code: r.code, label: r.name }))}
              selected={draft.regions}
              onToggle={(code) => toggle('regions', code)}
            />
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2 px-4">
          <button
            type="button"
            className="ui-pressable h-12 min-h-12 shrink-0 cursor-pointer rounded-xl border border-white-700 bg-white-50 px-[22px] py-3 text-body-md font-semibold text-black-600"
            onClick={() => setDraft(EMPTY)}
          >
            초기화
          </button>
          <button
            type="button"
            className="ui-pressable h-12 min-h-12 min-w-0 flex-1 shrink-0 grow basis-0 cursor-pointer rounded-xl border-0 bg-violet-500 px-4 py-3 text-body-md font-semibold text-white-50"
            onClick={() => requestClose(() => onApply(draft))}
          >
            적용하기
          </button>
        </div>
      </div>
    </div>
  )
}

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="m-0 text-label font-medium text-black-100">{label}</p>
      {children}
    </div>
  )
}

function ChipGroup({
  items,
  selected,
  onToggle,
}: {
  items: { code: string; label: string; half?: boolean }[]
  selected: string[]
  onToggle: (code: string) => void
}) {
  return (
    <div className="grid grid-cols-6 justify-items-stretch gap-x-2 gap-y-2.5 [&>.ui-selectbutton]:col-span-2 [&>.ui-selectbutton]:whitespace-nowrap [&>.ui-selectbutton:not(.is-on)]:text-black-400">
      {items.map((it) => (
        <Selectbutton
          key={it.code}
          label={it.label}
          isOn={selected.includes(it.code)}
          // 시안에서 한 줄을 둘이 나눠 갖는 것(167.5)
          className={it.half === true ? 'col-span-3' : ''}
          onSelect={() => onToggle(it.code)}
        />
      ))}
    </div>
  )
}
